import { useNavigate } from "@tanstack/react-router";
import { Plus, SearchMd } from "@untitledui/icons";
import { useState } from "react";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { WorkspaceLogo } from "@/components/admin/workspace-logo";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";

type Props = {
  merchantName: string;
  /** Currently selected workspace IDs (union of already-enrolled + newly toggled) */
  value: string[];
  onChange: (ids: string[]) => void;
  onNavigateAway?: () => void;
};

export const WorkspacesPicker = ({ merchantName, value, onChange, onNavigateAway }: Props) => {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const { data: workspacesData } = useAdminWorkspaces(
    { offset: 0, limit: 500 },
    { column: "name", direction: "asc" },
    {},
  );

  const selectedSet = new Set(value);

  const toggle = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const filtered = (workspacesData?.workspaces ?? []).filter((ws) =>
    ws.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleAddNew = () => {
    onNavigateAway?.();
    navigate({ to: "/admin/workspaces/new" });
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden gap-3">
      <h2 className="text-xl font-semibold text-primary shrink-0">Select workspaces {merchantName} should be in</h2>

      <div className="shrink-0">
        <Input
          placeholder="Search"
          value={search}
          onChange={setSearch}
          icon={SearchMd}
          wrapperClassName="bg-primary ring-primary rounded-xl"
          iconClassName="text-fg-quaternary"
          inputClassName="text-primary placeholder:text-placeholder"
        />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-2">
        {filtered.length === 0 && <p className="text-center text-sm text-tertiary py-6">No workspaces found</p>}

        {filtered.map((workspace) => {
          const isSelected = selectedSet.has(workspace.id);
          return (
            <button
              key={workspace.id}
              type="button"
              onClick={() => toggle(workspace.id)}
              className={`w-full shrink-0 flex items-center overflow-hidden rounded-xl border transition-colors hover:bg-white/5 ${
                isSelected ? "border-brand-600 bg-white/5" : "border-primary"
              }`}
            >
              <WorkspaceLogo name={workspace.name} logoUrl={workspace.logoUrl} className="w-14 h-14 rounded-l-xl" />
              <span className="flex-1 text-sm font-medium text-primary text-left px-3">{workspace.name}</span>
              <div className="pr-4 flex-shrink-0">
                <CheckboxBase size="md" isSelected={isSelected} />
              </div>
            </button>
          );
        })}

        <div className="shrink-0 flex items-center justify-between px-4 py-3 rounded-xl bg-secondary">
          <span className="text-sm text-primary">Workspace not here?</span>
          <button
            type="button"
            onClick={handleAddNew}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-solid text-white text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="size-4" />
            Add New Workspace
          </button>
        </div>
      </div>
    </div>
  );
};
